import React from "react";
import { Package, Trophy } from "@phosphor-icons/react";

function ScoreBar({ score, highlight }) {
  const val = Number(score) || 0;
  const pct = Math.max(0, Math.min(100, val * 10));
  return (
    <div className="w-full">
      <div className="flex items-baseline gap-1 mb-1">
        <span className={`text-sm font-bold ${highlight ? "text-[#0047AB]" : "text-zinc-900"}`}>
          {val.toFixed(1)}
        </span>
        <span className="text-[10px] text-zinc-400">/10</span>
      </div>
      <div className="h-1.5 w-full bg-zinc-100 overflow-hidden rounded-full">
        <div
          className={`h-full rounded-full transition-all ${highlight ? "bg-[#0047AB]" : "bg-zinc-400"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Shared, price-free comparison scorecard.
 * @param {{categories:Array, products:Array}} data
 * @param {(product)=>React.ReactNode} renderImage - product image node builder
 */
export default function ComparisonScorecard({ data, renderImage }) {
  const categories = data?.categories || [];
  const products = data?.products || [];
  if (categories.length === 0 || products.length < 2) return null;

  const cols = `minmax(150px, 190px) repeat(${products.length}, minmax(140px, 1fr))`;

  return (
    <section className="mt-12" data-testid="comparison-scorecard">
      <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight text-zinc-900">
        Comparison scorecard
      </h2>
      <p className="text-sm text-zinc-500 mb-6">
        Weighted rating out of 10 across {categories.length} categories. Higher is better.
      </p>

      <div className="border border-zinc-200 bg-white overflow-x-auto" data-testid="comparison-scorecard-table">
        <div style={{ minWidth: 190 + products.length * 140 }}>
          {/* Header row — product columns */}
          <div className="grid border-b border-zinc-200" style={{ gridTemplateColumns: cols }}>
            <div className="p-4 flex items-end">
              <span className="mt-tiny text-zinc-400 uppercase tracking-wider text-[11px]">Category</span>
            </div>
            {products.map((p) => (
              <div
                key={p.product_id}
                data-testid={`comparison-col-${p.product_id}`}
                className={`p-4 border-l border-zinc-100 flex flex-col items-center text-center ${
                  p.is_top_pick ? "bg-[#0047AB]/5" : ""
                }`}
              >
                <div className="relative w-16 h-16 bg-zinc-100 overflow-hidden mb-2">
                  {renderImage ? (
                    renderImage(p)
                  ) : (
                    <div className="w-full h-full grid place-items-center">
                      <Package size={24} className="text-zinc-400" weight="duotone" />
                    </div>
                  )}
                </div>
                {p.is_top_pick && (
                  <span
                    data-testid={`comparison-top-pick-${p.product_id}`}
                    className="inline-flex items-center gap-1 bg-[#FF5A1F] text-white text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full mb-1"
                  >
                    <Trophy size={11} weight="fill" /> Top pick
                  </span>
                )}
                <div className="text-sm font-bold text-zinc-900 leading-tight line-clamp-2">{p.name}</div>
                {p.brand && <div className="text-[11px] text-zinc-500 mt-0.5">{p.brand}</div>}
              </div>
            ))}
          </div>

          {/* Category rows */}
          {categories.map((c) => (
            <div
              key={c.category_id}
              className="grid border-b border-zinc-100"
              style={{ gridTemplateColumns: cols }}
              data-testid={`comparison-row-${c.category_id}`}
            >
              <div className="p-4 flex flex-col justify-center">
                <div className="text-sm font-medium text-zinc-800 leading-tight">{c.name}</div>
                <div className="text-[11px] text-zinc-400">Weight {c.weight}%</div>
              </div>
              {products.map((p) => (
                <div
                  key={p.product_id}
                  className={`p-4 border-l border-zinc-100 flex items-center ${p.is_top_pick ? "bg-[#0047AB]/5" : ""}`}
                >
                  <ScoreBar score={p.scores?.[c.category_id]} highlight={p.is_top_pick} />
                </div>
              ))}
            </div>
          ))}

          {/* Final weighted score row */}
          <div className="grid bg-zinc-50" style={{ gridTemplateColumns: cols }} data-testid="comparison-final-row">
            <div className="p-4 flex items-center">
              <div className="text-sm font-black uppercase tracking-wide text-zinc-900">Weighted score</div>
            </div>
            {products.map((p) => (
              <div
                key={p.product_id}
                data-testid={`comparison-final-${p.product_id}`}
                className={`p-4 border-l border-zinc-100 flex flex-col items-center justify-center ${
                  p.is_top_pick ? "bg-[#0047AB]/10" : ""
                }`}
              >
                <div className={`font-display text-3xl font-black ${p.is_top_pick ? "text-[#0047AB]" : "text-zinc-900"}`}>
                  {(Number(p.final_score) || 0).toFixed(1)}
                </div>
                <div className="text-[11px] text-zinc-400">out of 10</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
